/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Loader, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { SodMessage } from '../../../../../shared/components';
import {
  ENTITLEMENT_DEFAULT_FILTERS,
  RENDER_MODES,
  testIds,
  VIOLATIONS_INI_CALL,
  VIOLATIONS_TAB_LIST_AD_FILTER,
  violationsEntitlement,
} from '../../../../../shared/constants';
import {
  useGetQueryParams,
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../shared/hooks';
import { globalActions } from '../../../../../shared/store/globalSlice';
import { type ActiveFiltersState } from '../../../../../shared/types';
import ViolationsControls from '../../../../ViolationsTab/components/ViolationsControls';
import ViolationsInsights from '../../../../ViolationsTab/components/ViolationsInsights';
import { ViolationsListActionsManager } from '../../../../ViolationsTab/components/ViolationsListActions';
import ViolationsSidePanel from '../../../../ViolationsTab/components/ViolationsSidePanel';
import ViolationsTabFooter from '../../../../ViolationsTab/components/ViolationsTabFooter';
import ViolationsTabGraph from '../../../../ViolationsTab/components/ViolationsTabGraph';
import ViolationsTabList from '../../../../ViolationsTab/components/ViolationsTabList';
import { violationsTabActions } from '../../../../ViolationsTab/data-store/violationsTabSlice';
import {
  useColumnPicker,
  useGetSortMenu,
  usePageTracker,
  useSetRenderMode,
} from '../../../../ViolationsTab/hooks';
import { useGetSodViolationsCountByStatusMutation } from '../../../data-layer/detailsApi';
import { detailsActions } from '../../../data-store/detailsSlice';
import { useEntitlementDetails } from '../../../hooks';

/**
 * EntitlementViolationsTab
 *
 * A specialized violations tab component for entitlement details that manages and displays
 * violations data specific to a selected entitlement. It handles filter initialization,
 * tab count updates, and coordinates the display of entitlement-specific violations.
 *
 * @returns {ReactElement} Returns a section containing entitlement violations data visualization
 *
 * @example
 * <EntitlementViolationsTab />
 *
 * @dependencies
 * - react
 * - react-redux
 * - @saviynt/design-system
 *
 * @features
 * - Supports both list and graph view modes for entitlement violations
 * - Updates tab counts with entitlement-specific violation data
 * - Handles entitlement-specific filter initialization
 * - Integrates with violations insights and side panels
 * - Supports bulk actions with footer
 * - Automatically applies entitlement and risk filters
 *
 * @state
 * The component relies on the following Redux state:
 * - activeViolationsTab: Current active violations tab
 * - selectedEntitlement: Currently selected entitlement data
 * - renderMode: Current view mode (list/graph)
 * - tabFilter: Tab filters with counts
 * - activeFilters: Currently applied filters
 *
 * @hooks
 * - useGetSodViolationsCountByStatusMutation: For fetching violation counts by status
 * - useColumnPicker: For initializing view columns
 * - usePageTracker: For tracking initialization calls
 * - useSetRenderMode: For setting initial render mode
 * - useGetSortMenu: For enabling sort functionality
 * - useEntitlementDetails: For accessing entitlement details
 */

function EntitlementViolationsTab(): ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();
  const queryParams = useGetQueryParams();
  const entitlementDetails = useEntitlementDetails();
  const scrollRef = useRef<HTMLElement>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  // Set initial render mode

  useSetRenderMode({ type: 'entitlement' });

  const {
    user: { isMenuPinned },
    aag: {
      global: { activeViolationsTab, isGlobalLoading },
      violationsTab: {
        controls: { renderMode, isInsightsOpen, sortInAction, searchKey },
        list: { totalCount },
        bulkaction: { isFooterVisible },
        sidepanel: { isSidePanelVisible },
        actionManager,
      },
      details: {
        entitlementSummary: { selectedEntitlement },
        entitlementViolationsTab: {
          activeFilters,
          filters: existingEntitlementFilters,
        },
      },
    },
  } = useTypedSelector((state) => state);

  // Initial query hooks
  const { updateTracker } = usePageTracker(Object.values(VIOLATIONS_INI_CALL));

  const initializeAdvancedFilters = (
    existingFilters: {
      value: string;
      isDisabled?: boolean;
      isChecked?: boolean;
    }[] = []
  ) => {
    // Use only entitlement-specific default filters in the correct order
    const entitlementFilters = ENTITLEMENT_DEFAULT_FILTERS.map((filterValue) =>
      Object.values(VIOLATIONS_TAB_LIST_AD_FILTER).find(
        (filter) => filter.value === filterValue
      )
    )
      .filter((filter): filter is NonNullable<typeof filter> => Boolean(filter))
      .map((filter) => {
        // Find existing filter state
        const existingFilter = existingFilters.find(
          (f) => f.value === filter.value
        );

        // Make riskName, entitlement_value, and riskKeys checked and non-removable in entitlement context
        if (
          filter.value === 'riskName' ||
          filter.value === 'entitlement_value' ||
          filter.value === 'riskKeys'
        ) {
          return {
            ...filter,
            isDisabled: existingFilter?.isDisabled ?? false,
            isChecked: existingFilter?.isChecked ?? true,
            isNoRemoveFilterBtn: true,
          };
        }

        return {
          ...filter,
          isDisabled: existingFilter?.isDisabled ?? false,
          isChecked: existingFilter?.isChecked ?? true,
        };
      });

    dispatch(
      detailsActions.setEntitlementViolationsFilters(entitlementFilters)
    );

    return true;
  };

  const initializeTabFilters = () => {
    const tabFilters = [
      {
        label: l('Open'),
        value: 'openCount',
        count: 0,
        isChecked: true,
        isDisabled: false,
        color: 'critical' as const,
      },
      {
        label: l('Mitigated'),
        value: 'mitigatedCount',
        count: 0,
        isChecked: true,
        isDisabled: false,
        color: 'info' as const,
      },
      {
        label: l('In Review'),
        value: 'inReviewCount',
        count: 0,
        isChecked: true,
        isDisabled: false,
        color: 'warning' as const,
      },
      {
        label: l('In Remediation'),
        value: 'remediatedCount',
        count: 0,
        isChecked: true,
        isDisabled: false,
        color: 'neutral' as const,
      },
      {
        label: l('Closed'),
        value: 'closedCount',
        count: 0,
        isChecked: true,
        isDisabled: false,
        color: 'success' as const,
      },
    ];

    dispatch(detailsActions.setEntitlementViolationsTabFilters(tabFilters));

    return tabFilters;
  };

  const { initializeViewColumns } = useColumnPicker();

  const [getSodViolationsCountByStatus] =
    useGetSodViolationsCountByStatusMutation();

  // Function to update tab counts
  const updateTabCounts = useCallback(async () => {
    if (!selectedEntitlement?.entitlementValueKey) {
      return;
    }

    try {
      const entitlementKey = selectedEntitlement.entitlementValueKey;

      // Build filter map from active filters
      const activeFiltersMap = Object.fromEntries(
        activeFilters.map((f) => [f.value, f.options.map((o) => o.value)])
      );

      // Use the same filter construction logic as violations list
      const filterPayload = {
        risk: {
          riskIds:
            activeFiltersMap.riskName?.length > 0
              ? selectedEntitlement.riskObjects
                  .filter(
                    (risk) =>
                      risk.riskName &&
                      activeFiltersMap.riskName.includes(risk.riskName)
                  )
                  .map((risk) => risk.riskId)
                  .filter((id): id is number => typeof id === 'number')
              : selectedEntitlement.riskObjects
                  .map((risk) => risk.riskId)
                  .filter((id): id is number => typeof id === 'number'),
          riskName: activeFiltersMap.riskName,
        },
        entitlement: {
          entitlementValueKeys: [entitlementKey],
          value: activeFiltersMap.entitlement_value,
        },
        user: {
          userKeys:
            activeFiltersMap.systemUserName?.map((id) => Number(id)) || [],
          userName: activeFiltersMap.username,
          firstName: activeFiltersMap.firstname,
          lastName: activeFiltersMap.lastname,
        },
        globalSearch: searchKey ?? '',
      };

      // Remove undefined/empty properties, but preserve riskIds
      Object.keys(filterPayload).forEach((key) => {
        const section = filterPayload[key as keyof typeof filterPayload];

        if (typeof section === 'object') {
          Object.keys(section).forEach((subKey) => {
            // Don't remove riskIds as it's essential for the API call
            if (key === 'risk' && subKey === 'riskIds') {
              return;
            }

            if (
              Array.isArray(section[subKey as keyof typeof section]) &&
              (section[subKey as keyof typeof section] as unknown[]).length ===
                0
            ) {
              // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
              delete section[subKey as keyof typeof section];
            }
          });
        }
      });

      if (entitlementKey) {
        const tabCountResponse = await getSodViolationsCountByStatus({
          filters: filterPayload,
        }).unwrap();

        const currentTabFilters = [
          { label: l('Open'), value: 'openCount', color: 'critical' as const },
          {
            label: l('Mitigated'),
            value: 'mitigatedCount',
            color: 'info' as const,
          },
          {
            label: l('In Review'),
            value: 'inReviewCount',
            color: 'warning' as const,
          },
          {
            label: l('In Remediation'),
            value: 'remediatedCount',
            color: 'neutral' as const,
          },
          {
            label: l('Closed'),
            value: 'closedCount',
            color: 'success' as const,
          },
        ];

        const updatedTabFilters = currentTabFilters.map((tab) => ({
          ...tab,
          count: Number(
            tabCountResponse[tab.value as keyof typeof tabCountResponse] ?? 0
          ),
          isChecked: true,
          isDisabled: false,
        }));

        dispatch(
          detailsActions.setEntitlementViolationsTabFilters(updatedTabFilters)
        );

        // Update main violations count for header tab
        const totalViolationsCount = updatedTabFilters.reduce(
          (sum, tab) => sum + tab.count,
          0
        );

        dispatch(
          detailsActions.setEntitlementViolationsCount(totalViolationsCount)
        );
      }
    } catch {
      // Silently handle tab count update failure
    }
  }, [
    selectedEntitlement,
    activeFilters,
    searchKey,
    getSodViolationsCountByStatus,
    l,
    dispatch,
  ]);

  // Enable sort hook
  useGetSortMenu(sortInAction);

  useEffect(() => {
    if (activeViolationsTab === violationsEntitlement) {
      const initializeFilters = async () => {
        setIsInitializing(true);
        // Initialize filters and add entitlement-specific filters
        const [advancedFiltersResult, tabFiltersResult, viewColumnsResult] =
          await Promise.all([
            initializeAdvancedFilters(existingEntitlementFilters),
            initializeTabFilters(),
            initializeViewColumns(),
          ]);

        // Add entName, riskName, userName filter - use URL parameter if available, fallback to selectedEntitlement
        const urlRiskName = queryParams.riskName;
        const urlEntName =
          queryParams.entName || selectedEntitlement?.entitlementValue;
        const urlUserName = queryParams.userName;

        // Add entitlement filters to activeFilters after initialization
        if (
          selectedEntitlement?.entitlementValue &&
          selectedEntitlement.riskObjects.length > 0
        ) {
          const newActiveFilters: ActiveFiltersState[] = [];

          // Add entitlement_value filter
          newActiveFilters.push({
            value: 'entitlement_value',
            method: 'search',
            options: [
              {
                label: String(urlEntName),
                value: String(urlEntName),
                isChecked: true,
                isDisabled: true,
              },
            ],
          });

          const riskOptions = urlRiskName
            ? [
                {
                  label: urlRiskName,
                  value: urlRiskName,
                  isChecked: true,
                  isDisabled: false,
                },
              ]
            : selectedEntitlement.riskObjects
                .filter(
                  (riskObj): riskObj is { riskName: string } & typeof riskObj =>
                    typeof riskObj.riskName === 'string' &&
                    riskObj.riskName.length > 0
                )
                .map((riskObj) => ({
                  label: riskObj.riskName,
                  value: riskObj.riskName,
                  isChecked: true,
                  isDisabled: false,
                }));

          if (riskOptions.length > 0) {
            newActiveFilters.push({
              value: 'riskName',
              method: 'search',
              options: riskOptions,
            });
          }

          // Add riskKeys filter
          const riskKeysOptions = selectedEntitlement.riskObjects
            .filter(
              (riskObj): riskObj is { riskId: number } & typeof riskObj =>
                typeof riskObj.riskId === 'number' &&
                (urlRiskName ? riskObj.riskName === urlRiskName : true)
            )
            .map((riskObj) => ({
              label: String(riskObj.riskId),
              value: String(riskObj.riskId),
              isChecked: true,
              isDisabled: false,
            }));

          if (riskKeysOptions.length > 0) {
            newActiveFilters.push({
              value: 'riskKeys',
              method: 'search',
              options: riskKeysOptions,
            });
          }

          // Add userName filter if provided in URL
          if (urlUserName) {
            newActiveFilters.push({
              value: 'username',
              method: 'search',
              options: [
                {
                  label: urlUserName,
                  value: urlUserName,
                  isChecked: true,
                  isDisabled: false,
                },
              ],
            });
          }

          dispatch(
            detailsActions.setEntitlementViolationsActiveFilters(
              newActiveFilters
            )
          );
        }

        // Wait for filters to be properly initialized before updating counts
        if (advancedFiltersResult && viewColumnsResult) {
          // Update tab counts for entitlement tab after initialization
          try {
            const entitlementKey = selectedEntitlement?.entitlementValueKey;
            const riskIds = urlRiskName
              ? selectedEntitlement?.riskObjects
                  .filter((risk) => risk.riskName === urlRiskName)
                  .map((risk) => risk.riskId)
                  .filter((id): id is number => typeof id === 'number') ?? []
              : selectedEntitlement?.riskObjects
                  .map((risk) => risk.riskId)
                  .filter((id): id is number => typeof id === 'number') ?? [];

            if (entitlementKey && riskIds.length > 0) {
              const filterPayload: {
                risk: { riskIds: number[] };
                entitlement: { entitlementValueKeys: number[] };
                globalSearch: string;
                user?: { userName: string[] };
              } = {
                risk: { riskIds },
                entitlement: {
                  entitlementValueKeys: [entitlementKey],
                },
                globalSearch: '',
              };

              // Add userName filter if provided in URL
              if (urlUserName) {
                filterPayload.user = {
                  userName: [urlUserName],
                };
              }

              const tabCountResponse = await getSodViolationsCountByStatus({
                filters: filterPayload,
              }).unwrap();

              const updatedTabFilters = tabFiltersResult.map((tab) => ({
                ...tab,
                count: Number(
                  tabCountResponse[
                    tab.value as keyof typeof tabCountResponse
                  ] ?? 0
                ),
              }));

              dispatch(
                detailsActions.setEntitlementViolationsTabFilters(
                  updatedTabFilters
                )
              );
            }
          } catch {
            // Silently handle tab count update failure
          }
        }

        // Raise global error is any one fails
        const isFalseValue = !advancedFiltersResult || !viewColumnsResult;

        if (isFalseValue) {
          dispatch(globalActions.setGlobalError({ state: true }));
        }

        // Don't load page until all pass
        if (advancedFiltersResult && viewColumnsResult) {
          Object.values(VIOLATIONS_INI_CALL).forEach((callValue) => {
            updateTracker(callValue, true);
          });

          // Trigger search API call after all initialization is complete
          dispatch(violationsTabActions.setListIsRefresh(true));
        }

        setIsInitializing(false);
      };

      initializeFilters().catch(() => {
        setIsInitializing(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeViolationsTab,
    selectedEntitlement,
    queryParams.riskName,
    queryParams.entName,
    queryParams.userName,
  ]);

  const isGraphMode = useMemo(
    () => Boolean(renderMode === RENDER_MODES.GRAPH),
    [renderMode]
  );
  const isListMode = useMemo(
    () => Boolean(renderMode === RENDER_MODES.LIST),
    [renderMode]
  );

  // Handle filter adjustments when render mode changes
  useEffect(() => {
    if (!isInitializing && activeViolationsTab === violationsEntitlement) {
      dispatch(
        detailsActions.setEntitlementViolationsActiveFilters(activeFilters)
      );
    }
  }, [
    renderMode,
    isListMode,
    isGraphMode,
    isInitializing,
    activeViolationsTab,
    activeFilters,
    dispatch,
  ]);
  const renderListContent = useMemo(() => {
    if (!isGlobalLoading && isGraphMode) {
      return <ViolationsTabGraph />;
    }

    return (
      <>
        <ViolationsTabList />
        <ViolationsTabFooter />
      </>
    );
  }, [isGraphMode, isGlobalLoading]);

  const renderMainContent = useCallback(() => {
    if (entitlementDetails.isError) {
      return (
        <Box className='Sod-SidePanelSummary-loaderWrapper'>
          <Typography kind='body2'>
            {l('Error loading Violations details, please try again.')}
          </Typography>
          <Button
            type='button'
            kind='outlined'
            isEnabled
            onClick={() => {
              entitlementDetails.refetch().catch(() => {
                // Error handling is managed by the hook
              });
            }}>
            {l('Retry')}
          </Button>
        </Box>
      );
    }

    const hasOnlyEntitlementFilters = activeFilters.every(
      (filter) =>
        (filter as { value: string }).value === 'entitlement_value' &&
        (filter as { options: { isDisabled?: boolean }[] }).options.every(
          (option) => option.isDisabled
        )
    );

    return (
      <>
        <ViolationsControls />
        {totalCount === 0 && hasOnlyEntitlementFilters ? (
          <SodMessage
            isSearchActive={false}
            isFilterActive={false}
            handleFilterClearBtn={() => {}}
            defaultTitle={l('No Violations')}
            defaultMessage={l('No violations found.')}
          />
        ) : (
          renderListContent
        )}
      </>
    );
  }, [entitlementDetails, activeFilters, totalCount, l, renderListContent]);

  // Update tab counts when filters change (e.g., when cleared)
  useEffect(() => {
    if (
      activeViolationsTab === violationsEntitlement &&
      selectedEntitlement &&
      !isInitializing
    ) {
      // Trigger count update whenever active filters change
      // This will handle both clearing filters and changing filter values
      updateTabCounts().catch(() => {
        // Silently handle tab count update failure
      });
    }
  }, [
    activeFilters,
    searchKey,
    queryParams.userName,
    queryParams.riskName,
    activeViolationsTab,
    selectedEntitlement,
    isInitializing,
    updateTabCounts,
  ]);

  //  Scroll to top when gone to list page after scrolling in graph mode
  useEffect(() => {
    if (isListMode) {
      scrollRef.current?.scrollTo(0, 0);
    }
  }, [isListMode]);

  const violationsTabClasses = useMemo(
    () => cx('Sod-ViolationsTab', isListMode && 'Sod-ViolationsTab--isList'),
    [isListMode]
  );
  const violationsTabViewClasses = useMemo(
    () =>
      cx(
        'Sod-ViolationsTab-view',
        isGraphMode && 'Sod-ViolationsTab-view--isGraph',
        isListMode && 'Sod-ViolationsTab-view--isList',
        isSidePanelVisible && 'Sod-ViolationsTab-view--isSidePanelVisible',
        isInsightsOpen &&
          isGraphMode &&
          'Sod-ViolationsTab-view--isInsightsOpen',
        isFooterVisible && 'Sod-ViolationsTab-view--isFooterVisible',
        !isSidePanelVisible &&
          !isInsightsOpen &&
          isGraphMode &&
          'Sod-ViolationsTab-view--isNotAsides',
        isMenuPinned && 'Sod-ViolationsTab-view--isMenuPinned'
      ),
    [
      isGraphMode,
      isListMode,
      isSidePanelVisible,
      isInsightsOpen,
      isFooterVisible,
      isMenuPinned,
    ]
  );

  return (
    <section
      ref={scrollRef}
      className={violationsTabClasses}
      data-testid={testIds.SOD_VIOLATIONS_PAGE_BODY}>
      <Box
        className={violationsTabViewClasses}
        dataTheme={isGraphMode ? 'dark' : undefined}>
        {isGlobalLoading || isInitializing ? (
          <Box className='Sod-Violations-loaderWrapper'>
            <Loader kind='dots' format='inline' color='brand' />
          </Box>
        ) : (
          <Box className='Sod-ViolationsView-wrapper'>
            <Box className='Sod-ViolationsView-content'>
              {renderMainContent()}
            </Box>

            {isGraphMode || isSidePanelVisible ? (
              <Box className='Sod-ViolationsView-asides'>
                {isGraphMode && (
                  <ViolationsInsights className='Sod-ViolationsView-asidesInsight' />
                )}
                <ViolationsSidePanel />
              </Box>
            ) : null}
          </Box>
        )}
      </Box>
      {/* Actions */}
      {actionManager?.isOpen && (
        <ViolationsListActionsManager
          // eslint-disable-next-line react/jsx-props-no-spreading -- need to pass all the action properties
          {...actionManager}
        />
      )}
    </section>
  );
}

export default memo(EntitlementViolationsTab);
